/**
 * ⌘K's "This project" section: every tab of the open project, its services and its actions, ranked before the rest
 * of the palette. The project header (UnifiedTopBar) registers a source while a project is open
 * (`useProjectCommandSource`); the palette reads it when it opens (`readProjectCommands`), so the items always carry
 * the header's own handlers (the tab row's `onSelect`, the group menus' actions, Pin, Tab settings) and are gated the
 * way the header gates them (the plan's hidden tabs, the person's role, Chat in the plan).
 *
 * The tabs come from the tab model (utils/projectTabsModel.js) at runtime: their group ("Build › Configuration") is the
 * one the model gives today, whatever group a tab moves to.
 */
import { useEffect, useRef } from 'react';
import {
  ADVANCED_TABS, BUILD_TABS, HISTORY_SUBS, TAB_GROUPS, isTabHidden, tabHeading,
} from '../../utils/projectTabsModel';
import { isRunning } from '../../services/SandboxService';
import { suggestionsLabel } from '../../utils/collab3Model';
import { defaultAppOf, defaultRunnableOf } from '../../utils/defaultApp';
import { servicePath } from '../../utils/projectOverview';
import { isNoBuild } from '../../utils/repoFlags';
import { parseRunnableName } from '../../utils/runnablesModel';
import { appItem, openApp, startProject, stopProject, withDefaultRunnable } from '../sandbox/projectRun';
import { clearStart, isStarting, projectStartKey, runStart, stopStart } from '../sandbox/startRuns';

/** Other words people use for a tab or an action ("env" finds Configuration, "deploy" finds Publish). */
export const COMMAND_SYNONYMS = {
  dashboard: ['home', 'overview', 'start', 'project'],
  apps: ['services', 'service', 'containers', 'run'],
  files: ['brain', 'documents', 'docs', 'inputs', 'results', 'uploads'],
  preview: ['run', 'live', 'browser', 'app', 'see'],
  code: ['editor', 'source', 'ide', 'edit'],
  data: ['database', 'db', 'records', 'tables', 'datastore', 'sql', 'mongo', 'postgres'],
  config: ['env', 'environment', 'variables', 'vars', 'secrets', 'settings', 'keys', 'dotenv'],
  api: ['endpoints', 'routes', 'rest', 'http', 'request', 'openapi', 'swagger'],
  mcp: ['tools', 'inspector', 'model context protocol'],
  traces: ['tracing', 'requests', 'latency', 'spans', 'otel', 'opentelemetry', 'timings'],
  logs: ['output', 'stdout', 'stderr', 'console', 'build log'],
  jobs: ['cron', 'schedule', 'scheduled', 'tasks', 'timer'],
  finops: ['cost', 'costs', 'billing', 'spend', 'price', 'credits', 'money'],
  quality: ['tests', 'test', 'lint', 'security', 'vulnerabilities', 'licences', 'licenses', 'coverage', 'checks'],
  git: ['repository', 'repo', 'commit', 'commits', 'push', 'pull', 'branch', 'tag', 'clone'],
  mail: ['email', 'smtp', 'external', 'integrations', 'crm', 'messaging'],
  terminal: ['shell', 'console', 'bash', 'command line', 'cli', 'tty'],
  history: ['commits', 'changes', 'timeline', 'past', 'versions', 'undo'],
  changes: ['commits', 'diff', 'history', 'edits', 'modified'],
  activity: ['history', 'ai', 'agents', 'what happened'],
  sessions: ['history', 'ai sessions', 'tool sessions', 'agents', 'claude'],
  publish: ['deploy', 'deploys', 'release', 'ship', 'production', 'go live', 'publish'],
  share: ['invite', 'access', 'link', 'collaborators', 'people', 'permissions'],
  messages: ['message', 'dm', 'talk', 'team chat'],
  notes: ['comments', 'suggestions', 'handoff', 'leave a note', 'review', 'team notes'],
  'notes-comments': ['threads', 'review', 'discussion'],
  'notes-suggestions': ['proposals', 'review', 'changes proposed'],
  'notes-leave': ['notify', 'message', 'leave a note', 'note'],
  'notes-handoff': ['handoff', 'what next', 'notes'],
  backups: ['backup', 'restore', 'snapshot'],
  usage: ['plan', 'credits', 'limits', 'quota'],
  'start-all': ['run', 'launch', 'up', 'boot', 'start'],
  'stop-all': ['halt', 'down', 'kill', 'shutdown', 'stop'],
  'open-app': ['launch', 'url', 'new tab', 'visit', 'running app'],
  'open-preview': ['run', 'live', 'see the app'],
  chat: ['assistant', 'ai', 'turn on chat', 'flow assistant'],
  pin: ['keep', 'tab row', 'unpin'],
  'tab-settings': ['tabs', 'preferences', 'customize', 'customise', 'which tabs'],
};

const groupLabelOf = (groupId) => TAB_GROUPS.find((group) => group.id === groupId)?.label || null;

/**
 * Every tab of the project as a "Go to <Tab>" command: Home, the advanced tabs (Apps, Files, Code, Logs…), Preview,
 * History and its views (Changes, Activity, Sessions). A tab the plan hides (`hidden`, useProjectTabs) is left out,
 * Preview while there is no service to preview (`previewDisabled`). `select(id)` opens a tab: the tab row's handler.
 */
export function tabCommands({ hidden = [], select, previewDisabled = false, current = null } = {}) {
  if (typeof select !== 'function') { return []; }
  const home = BUILD_TABS.find((tab) => tab.id === 'dashboard');
  const preview = BUILD_TABS.find((tab) => tab.id === 'preview');
  const list = [
    { id: 'dashboard', label: home?.label || 'Home', group: null, desc: home?.desc || 'The project overview' },
    ...ADVANCED_TABS.map((tab) => ({ id: tab.id, label: tab.label, group: groupLabelOf(tab.group), desc: tab.desc })),
    ...(previewDisabled ? [] : [{
      id: 'preview', label: preview?.label || 'Preview', group: tabHeading('preview')?.groupLabel || null, desc: tabHeading('preview')?.desc || preview?.desc || '',
    }]),
    { id: 'history', label: 'History', group: tabHeading('history')?.groupLabel || null, desc: tabHeading('history')?.desc || '' },
    ...HISTORY_SUBS.map((sub) => ({
      id: sub.id, label: sub.label, group: [tabHeading('history')?.groupLabel, 'History'].filter(Boolean).join(' › '), desc: `${sub.label} of the project, in History.`,
    })),
  ];
  return list
    .filter((tab) => !isTabHidden(hidden, tab.id))
    .map((tab) => ({
      key: `here-tab-${tab.id}`,
      kind: 'tab',
      id: tab.id,
      label: tab.label,
      title: `Go to ${tab.label}`,
      group: tab.group,
      desc: tab.desc,
      subtitle: [tab.group ? `${tab.group} › ${tab.label}` : tab.label, tab.desc].filter(Boolean).join(' · '),
      synonyms: COMMAND_SYNONYMS[tab.id] || [],
      current: current === tab.id,
      run: () => select(tab.id),
    }));
}

const runsNow = (entry) => Boolean(entry) && entry.containerStatus === 'running' && entry.buildStatus !== 'building';

/**
 * The header's services for "Open <service> app": its service picker's `items` (`{ key, value, title }`) and the files
 * pinned in a service that the runner knows (`runner`, their own sessions, Apps lists them under it), each
 * `{ value, title, running, inApps, runnable?, open }`. `open` lands on Apps with that service selected
 * (`openInApps(serviceName)`, as clicking it there does); where the plan hides Apps (`openInApps` null) a running one
 * opens its app (`openApp(serviceName, entry)`), a stopped service Preview on it (`openPreview(item)`), a stopped pinned
 * file the Apps tab (`openApps()`). A native device app (`deviceOf(serviceName, entry)`, utils/deviceApps.js: an
 * iPhone or Android app, never running in the sandbox) has `device` and opens its page in Apps with its builds, or
 * where the plan hides Apps the Preview, which shows how to build it; never "Not running".
 */
export function projectServiceItems({
  items = [], runner = [], openInApps = null, openApp = () => {}, openPreview = () => {}, openApps = () => {}, deviceOf = () => null,
} = {}) {
  const runnerOf = (name) => runner.find((entry) => entry.serviceName === name) || null;
  const inApps = typeof openInApps === 'function';
  return [
    ...items.map((item) => {
      const serviceName = servicePath(item.key);
      const entry = runnerOf(serviceName);
      const device = deviceOf(serviceName, entry) || null;
      const running = !device && runsNow(entry);
      return {
        value: item.value,
        title: item.title,
        running,
        inApps,
        ...(device ? { device } : {}),
        open: () => {
          if (inApps) { openInApps(serviceName); return; }
          if (running) { openApp(serviceName, entry); return; }
          openPreview(item);
        },
      };
    }),
    ...runner.filter((entry) => parseRunnableName(entry.serviceName)).map((entry) => {
      const pinned = parseRunnableName(entry.serviceName);
      const running = runsNow(entry);
      return {
        value: entry.serviceName,
        title: `${String(pinned.service).replace(/^services\//, '')} · ${pinned.kind === 'compose' ? 'Compose' : 'Dockerfile'}`,
        runnable: true,
        running,
        inApps,
        open: () => {
          if (inApps) { openInApps(entry.serviceName); return; }
          if (running) { openApp(entry.serviceName, entry); return; }
          openApps();
        },
      };
    }),
  ];
}

/**
 * The project's services, "Open <service> app": `[{ value, title, running, open, inApps }]` from the header (its
 * service picker's items with the runner's state); `open` is the header's own handler: Apps with the service selected
 * (`inApps`), else, where the plan hides Apps, the running app in a new tab or Preview. Pinned files the runner knows
 * come the same way, `runnable: true`.
 */
/** A device app's line: "iPhone app: its page in Apps, with its builds" (where the plan hides Apps: how to build it). */
function deviceDesc(service) {
  const platforms = service.device.platforms || [];
  const kind = platforms.length === 1 ? (platforms[0] === 'ios' ? 'iPhone app' : 'Android app') : 'Mobile app';
  return service.inApps ? `${kind}: its page in Apps, with its builds` : `${kind}: runs on a device; opens how to build it`;
}

export function serviceCommands(services = []) {
  return services.filter((service) => service && typeof service.open === 'function').map((service) => ({
    key: `here-service-${service.value}`,
    kind: 'service',
    id: service.value,
    label: service.title,
    title: `Open ${service.title} app`,
    group: service.runnable ? 'Apps › pinned file' : 'Apps',
    desc: service.device ? deviceDesc(service)
      : service.inApps ? (service.running ? 'Running: its page in Apps, with Open app' : 'Not running: its page in Apps, with Start')
        : service.running ? 'Running: opens the app in a new tab' : 'Not running: opens it in Preview',
    subtitle: `${service.runnable ? 'Apps › pinned file' : 'Apps › service'} · ${service.device ? service.device.chip || 'Device app' : service.running ? 'running' : 'stopped'}`,
    synonyms: ['service', 'app', 'open', ...(service.runnable ? ['compose', 'dockerfile'] : []), ...(service.device ? ['build', 'device', 'mobile', 'phone'] : [])],
    live: Boolean(service.running),
    run: service.open,
  }));
}

/**
 * The project's actions, each only when the header offers it to this person: `[{ id, label, desc, onSelect }]`, the
 * group menus' actions (`groupActions`, already gated by role and plan) and the header's own (Start all, Open app…).
 */
export function actionCommands(actions = []) {
  const seen = new Set();
  return actions.filter((action) => action && typeof action.onSelect === 'function' && !seen.has(action.id) && seen.add(action.id)).map((action) => ({
    key: `here-action-${action.id}`,
    kind: 'action',
    id: action.id,
    label: action.label,
    title: action.label,
    group: action.group || null,
    desc: action.desc || '',
    subtitle: [action.group, action.desc].filter(Boolean).join(' · ') || null,
    synonyms: COMMAND_SYNONYMS[action.synonymsOf || action.id] || [],
    run: action.onSelect,
  }));
}

/**
 * Team ▾ › Notes' sections as actions, each opening Notes on its tab (`open(tab)`, collab/NotesModal.jsx): Comments,
 * Suggestions (n) with `pending` waiting, Leave a note…, Handoff notes. `group`: Team's label ("Team › Notes").
 */
export function notesCommands({ open, pending = 0, group = 'Team' } = {}) {
  if (typeof open !== 'function') { return []; }
  return [
    { id: 'comments', label: 'Comments', desc: 'Every comment thread on the project\'s files' },
    { id: 'suggestions', label: suggestionsLabel(pending), desc: 'Changes proposed with Suggest a change; editors accept or decline them' },
    { id: 'leave', label: 'Leave a note…', desc: 'A message to people on this project; they get a notification' },
    { id: 'handoff', label: 'Handoff notes', desc: 'What was done and what is next, the notes sent and received' },
  ].map((section) => ({
    ...section, id: `notes-${section.id}`, group: [group, 'Notes'].filter(Boolean).join(' › '), onSelect: () => open(section.id),
  }));
}

/**
 * The header's run control as commands (ProjectRunTools / RunAllActions): Start all while something is stopped, Stop
 * all while something runs, Open app while an app runs. The same items and the same start registry
 * (sandbox/startRuns.js), so the header's Start button follows a start begun here. `canRun`: false for a viewer
 * (useProjectServices), who gets Open app only. `serviceKeys`: the configuration's keys ('web', 'services/api').
 */
export function runCommands({
  projectId = null, serviceKeys = [], runnerServices = [], canRun = false, onChanged = null, onError = null,
} = {}) {
  if (!projectId || serviceKeys.length === 0) { return []; }
  // The services, with the default app when it is a pinned file (started with its payload), as the header has them.
  const items = withDefaultRunnable(serviceKeys.map((key) => {
    const serviceName = servicePath(key);
    return { serviceName, service: runnerServices.find((entry) => entry.serviceName === serviceName) || null };
  }), defaultRunnableOf(projectId), runnerServices);
  const single = items.length === 1;
  const toStart = items.filter((item) => !isRunning(item.service) && !isNoBuild(projectId, item.serviceName));
  const toStop = items.filter((item) => isRunning(item.service));
  const key = projectStartKey(projectId);
  const out = [];
  if (canRun && toStart.length > 0 && !isStarting(key)) {
    out.push({
      id: 'start-all',
      label: single ? 'Start' : 'Start all',
      group: 'Run',
      desc: single ? 'Start the app' : `Start the ${toStart.length} service${toStart.length === 1 ? '' : 's'} that do not run`,
      onSelect: () => {
        // Its progress and a refusal (the plan's limit) show on the header's Start button, as for a start from there.
        runStart(key, (onProgress, shouldStop) => startProject(projectId, items, { onProgress, shouldStop }))
          .catch((err) => onError?.(err?.message || 'The project could not start'))
          .finally(() => onChanged?.());
      },
    });
  }
  if (canRun && toStop.length > 0) {
    out.push({
      id: 'stop-all',
      label: single ? 'Stop' : 'Stop all',
      group: 'Run',
      desc: single ? 'Stop the app' : `Stop the ${toStop.length} service${toStop.length === 1 ? '' : 's'} that run`,
      onSelect: () => {
        stopStart(key);
        clearStart(key);
        stopProject(projectId, toStop)
          .then(({ error }) => { if (error) { onError?.(error); } })
          .finally(() => onChanged?.());
      },
    });
  }
  const app = appItem(projectId, items, defaultAppOf(projectId));
  if (app) {
    out.push({ id: 'open-app', label: 'Open app', group: 'Run', desc: 'The running app, in a new tab', onSelect: () => openApp(projectId, app) });
  }
  return out;
}

/** The whole section, in the order shown with nothing typed: actions, tabs, services. */
export function buildProjectCommands({
  projectName = null, tabs = {}, services = [], actions = [],
} = {}) {
  return {
    projectName,
    items: [...actionCommands(actions), ...tabCommands(tabs), ...serviceCommands(services)],
  };
}

// ---- matching -------------------------------------------------------------------------------------------------------

const words = (text) => String(text || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const lettersInOrder = (hay, word) => {
  let i = 0;
  for (const c of hay) { if (c === word[i]) { i += 1; } }
  return i >= word.length;
};

/**
 * How well one typed word fits a command: the start of its name 10, the start of a word of its name 8, of its title
 * ("Go to …") 7, the start of a synonym 6, of its group 4, of a word of its description 3, inside its name 2, its name's letters in order (fuzzy,
 * 3+ letters) 1; 0: nowhere.
 */
function wordScore(item, word) {
  const label = String(item.label || '').toLowerCase();
  if (label.startsWith(word)) { return 10; }
  if (words(label).some((w) => w.startsWith(word))) { return 8; }
  // "Go to …": `go` and `to` fit every tab, so "go con" is Configuration.
  if (words(item.title).some((w) => w.startsWith(word))) { return 7; }
  if ((item.synonyms || []).some((s) => String(s).toLowerCase().startsWith(word) || words(s).some((w) => w.startsWith(word)))) { return 6; }
  if (words(item.group).some((w) => w.startsWith(word))) { return 4; }
  if (words(item.desc).some((w) => w.startsWith(word))) { return 3; }
  if (label.includes(word)) { return 2; }
  if (word.length >= 3 && lettersInOrder(label.replace(/\s+/g, ''), word)) { return 1; }
  return 0;
}

/** A command's score for the query: every word must fit somewhere (0 otherwise); higher is better. */
export function scoreCommand(item, query) {
  const typed = words(query);
  if (typed.length === 0) { return 1; }
  let total = 0;
  for (const word of typed) {
    const score = wordScore(item, word);
    if (!score) { return 0; }
    total += score;
  }
  // A whole-phrase start of the name ("start all", "go to con") beats words found apart.
  const phrase = typed.join(' ');
  if (String(item.label || '').toLowerCase().startsWith(phrase) || String(item.title || '').toLowerCase().startsWith(phrase)) { total += 2; }
  return total;
}

/** The commands that fit `query`, best first (a stable order between equals: the section's own). */
export function rankProjectCommands(items = [], query = '') {
  return items
    .map((item, index) => ({ item, index, score: scoreCommand(item, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.item);
}

// ---- the open project's source ------------------------------------------------------------------------------------

const sources = [];

/** The open project's commands (the latest source registered), or null outside a project. */
export function readProjectCommands() {
  for (let i = sources.length - 1; i >= 0; i -= 1) {
    try {
      const value = sources[i].current?.();
      if (value && Array.isArray(value.items)) { return value; }
    } catch { /* a source that cannot answer adds nothing */ }
  }
  return null;
}

/** For tests: a source without a component. Returns the function that removes it. */
export function registerProjectCommands(build) {
  const entry = { current: build };
  sources.push(entry);
  return () => { const at = sources.indexOf(entry); if (at >= 0) { sources.splice(at, 1); } };
}

/**
 * Registers the open project's commands while the component is mounted. `build` is called when the palette opens, with
 * what the component rendered last (so it never runs on every render); it answers `buildProjectCommands(...)` or null.
 */
export function useProjectCommandSource(build) {
  const ref = useRef(build);
  ref.current = build;
  useEffect(() => registerProjectCommands(() => ref.current?.()), []);
}
